import get from 'lodash/get';
import {createSelector} from 'reselect';
import isEmpty from 'lodash/isEmpty';
import {Views} from 'conversional-big-calendar';

import {filterNullUndefined} from '@/utils/functions';
import {FILTER_PLACES} from '@/components/Filters/LeadFilters';
import * as PerformanceReportConstants from '@/pages/LeadManagementReports/constants';
import {
  APPOINTMENT_PROPERTIES,
  CALENDAR_CARD_PROPERTIES,
  CALENDAR_PROPERTIES,
  DEFAULT_VISIBILITY_SETTINGS,
  INITIAL_PROPERTY_VISIBILITY_SETTINGS,
  LEAD_PROPERTIES,
  LEAD_VISIBILITY_SETTINGS_PLACES,
} from '@/components/Kanban/VisibilitySettings/constants';
import {CUSTOMER_PROPERTIES, CUSTOMER_VISIBILITY_SETTINGS_PLACES} from '@/components/CustomerDrawer/constants';
import {PARTNER_CONFIG_KEYS, SAVED_VIEW_SCOPES} from '@/components/Preferences/constants';
import * as ServiceRequestSelectors from '@/selectors/serviceRequestSelectors';
import dayjs from 'dayjs';
import {WEEK_AGENDA_VIEW} from '@/components/Kanban/ServiceRequests/Calendar/WeekAgendaView';
import {CalendarUISettings, sanitizeCalendarUISettings} from '@/typings/models/CalendarUISettings';

// Saved preferences outlive the code: a property removed from the constants
// below stays in every user's stored shown/hidden arrays, where it would render
// a field label with no column behind it. Dropping anything the app no longer
// knows means removing a property is the whole removal — nothing to deprecate.
const KNOWN_PROPERTIES = new Set<string>([
  ...Object.values(LEAD_PROPERTIES),
  ...Object.values(CUSTOMER_PROPERTIES),
  ...Object.values(APPOINTMENT_PROPERTIES),
  ...Object.values(CALENDAR_PROPERTIES),
  ...Object.values(CALENDAR_CARD_PROPERTIES),
]);
// Extra fields are partner-defined, so they have no constant to match against.
const EXTRA_FIELD_PREFIX = 'extraField:';
const filterProperties = (property: string) =>
  Boolean(property) &&
  (KNOWN_PROPERTIES.has(property) || property.startsWith(EXTRA_FIELD_PREFIX));

const EMPTY_ARRAY = Object.freeze([]);
const EMPTY_OBJECT = Object.freeze({});

// region Lead Visibility Settings

export const selectLeadsVisibilitySettings = createSelector(
  [
    (
      state: DefaultRootState,
      place: string = LEAD_VISIBILITY_SETTINGS_PLACES.LEAD_BOARD,
    ) => get(state, `preferences.list.ui.${place}`),
    (
      _state: DefaultRootState,
      place: string = LEAD_VISIBILITY_SETTINGS_PLACES.LEAD_BOARD,
    ) => place,
  ],
  (settingsFromPreferences, place) => {
    const DEFAULT_SETTINGS =
      DEFAULT_VISIBILITY_SETTINGS[place] ||
      INITIAL_PROPERTY_VISIBILITY_SETTINGS;

    if (isEmpty(settingsFromPreferences)) return DEFAULT_SETTINGS;

    const preferencesFields = []
      .concat(
        settingsFromPreferences.shown || [],
        settingsFromPreferences.hidden || [],
      )
      .filter(Boolean);

    const ALL_DEFAULT_PROPERTIES = []
      .concat(DEFAULT_SETTINGS.shown || [], DEFAULT_SETTINGS.hidden || [])
      .filter(Boolean);

    const notInPreferences = ALL_DEFAULT_PROPERTIES.filter(
      (field) => !preferencesFields.includes(field),
    ).filter(Boolean);

    return {
      shown: (settingsFromPreferences.shown || []).filter(filterProperties),
      hidden: (settingsFromPreferences.hidden || [])
        .concat(notInPreferences)
        .filter(filterProperties),
    };
  },
);

export const selectShownLeadsProperties = createSelector(
  selectLeadsVisibilitySettings,
  (settings) => (settings?.shown || []).filter(filterProperties)
);

export const selectHiddenLeadsProperties = createSelector(
  selectLeadsVisibilitySettings,
  (settings) => (settings?.hidden || []).filter(filterProperties)
);

export const selectShownLeadsTableProperties = createSelector(
  (state: DefaultRootState) => selectLeadsVisibilitySettings(state, LEAD_VISIBILITY_SETTINGS_PLACES.LEAD_TABLE),
  (settings) => settings?.shown || EMPTY_ARRAY
);

export const selectShownProperties = createSelector(
  (state: DefaultRootState, place: string) => selectLeadsVisibilitySettings(state, place),
  (settings) => settings?.shown || EMPTY_ARRAY
);

export const selectHiddenProperties = createSelector(
  (state: DefaultRootState, place: string) => selectLeadsVisibilitySettings(state, place),
  (settings) => settings?.hidden || EMPTY_ARRAY
);

export const selectAllLeadsProperties = createSelector(
  [selectShownLeadsProperties, selectHiddenLeadsProperties],
  (shown, hidden) => shown.concat(hidden)
);
// endregion

// region Customer Visibility Settings
export const selectShownCustomersTableProperties = createSelector(
  (state: DefaultRootState) => selectLeadsVisibilitySettings(state, CUSTOMER_VISIBILITY_SETTINGS_PLACES.CUSTOMERS_TABLE),
  (settings) => settings?.shown || EMPTY_ARRAY
);
// endregion

// region Calendar Visibility Settings
export const selectShownCalendarEventPopoverProperties = createSelector(
  (state: DefaultRootState) => selectLeadsVisibilitySettings(state, LEAD_VISIBILITY_SETTINGS_PLACES.CALENDAR_EVENT_POPOVER),
  (settings) => settings?.shown || EMPTY_ARRAY
);

export const selectShownCalendarEventCardProperties = createSelector(
  (state: DefaultRootState) => selectLeadsVisibilitySettings(state, LEAD_VISIBILITY_SETTINGS_PLACES.CALENDAR_CARD),
  (settings) => settings?.shown || EMPTY_ARRAY
);
// endregion

// region Lead View Mode
export const selectLeadView = createSelector(
  (state: DefaultRootState) => state?.preferences?.list?.ui?.leadListView?.mode,
  (leadView) => {
    const mode = leadView || 'kanban';
    if (mode === 'calendar') return 'kanban'; // Deprecate lead calendar view in Apr 2025
    return mode;
  }
);

/** The view each view area had open last — see `constants/viewAreas`. */
export const selectLastAreaViews = (state: DefaultRootState) =>
  state?.preferences?.list?.ui?.lastAreaViews ?? EMPTY_OBJECT;
// endregion

// region Filters and Place Preferences
const BRANCH_FILTER_KEYS = ['branch', 'calendarBranch'];
const NO_BRANCH_ID = 'null';

// Saved views (esp. partner-wide ones) can carry branches the current user has no access to;
// those are dropped so the filter falls back to the user's own branches.
const dropInaccessibleBranches = (filters: any, accessibleBranches: any[]) => {
  if (isEmpty(accessibleBranches)) return filters;
  const accessibleIds = new Set(accessibleBranches.map(branch => branch.id));
  const isAccessible = (id: string) => id === NO_BRANCH_ID || accessibleIds.has(id);

  BRANCH_FILTER_KEYS.forEach((key) => {
    const value = filters[key];
    if (Array.isArray(value)) {
      const accessible = value.filter(isAccessible);
      if (accessible.length !== value.length) filters[key] = accessible;
    } else if (typeof value === 'string' && !isAccessible(value)) {
      delete filters[key];
    }
  });
  return filters;
};

// Create a memoized selector factory for filters by place
const createSelectFilters = (place: string) => createSelector(
  (state: DefaultRootState) => get(state, `preferences.list.ui.${place}.filters`, EMPTY_OBJECT),
  (state: DefaultRootState) => get(state, 'location.allBranchesList', EMPTY_ARRAY),
  (filters, accessibleBranches) => {
    const processed = dropInaccessibleBranches(filterNullUndefined(filters), accessibleBranches);

    if (place === FILTER_PLACES.LEAD_PERFORMANCE_REPORT) {
      processed.metrics = processed.metrics || PerformanceReportConstants.DEFAULT_METRICS;
      processed.group_by = processed.group_by || PerformanceReportConstants.DEFAULT_GROUP;
    }

    return processed;
  }
);

// Keep the original function interface for backward compatibility
// but use individual memoized selectors per place
const filtersCache = new Map<string, ReturnType<typeof createSelectFilters>>();

export const selectFilters = (state: DefaultRootState, place: string) => {
  if (!filtersCache.has(place)) {
    filtersCache.set(place, createSelectFilters(place));
  }
  return filtersCache.get(place)!(state);
};

const createSelectPlacePreferences = (place: string) => createSelector(
  (state: DefaultRootState) => get(state, `preferences.list.ui.${place}.preferences`, EMPTY_OBJECT),
  (preferences) => preferences
);

const placePreferencesCache = new Map<string, ReturnType<typeof createSelectPlacePreferences>>();

export const selectPlacePreferences = (state: DefaultRootState, place: string) => {
  if (!placePreferencesCache.has(place)) {
    placePreferencesCache.set(place, createSelectPlacePreferences(place));
  }
  return placePreferencesCache.get(place)!(state);
};

const createSelectPlaceViewSettings = (place: string) => createSelector(
  [
    (state: DefaultRootState) => selectFilters(state, place),
    (state: DefaultRootState) => selectPlacePreferences(state, place),
  ],
  (filters, preferences) => {
    // Ensure sorting is always part of the savable filters, even if some UIs stored it under preferences historically
    const mergedFilters = {
      ...filters,
      ...(!filters?.sort && (preferences as any)?.sort ? { sort: (preferences as any).sort } : {}),
    };
    return {
      filters: mergedFilters,
      preferences,
    };
  }
);

const placeViewSettingsCache = new Map<string, ReturnType<typeof createSelectPlaceViewSettings>>();

export const selectPlaceViewSettings = (state: DefaultRootState, place: string) => {
  if (!placeViewSettingsCache.has(place)) {
    placeViewSettingsCache.set(place, createSelectPlaceViewSettings(place));
  }
  return placeViewSettingsCache.get(place)!(state);
};

export const selectCalendarView = createSelector(
  (state: DefaultRootState) => selectPlacePreferences(state, FILTER_PLACES.TEAM_VIEW_CALENDAR).view?.value,
  (viewValue) => viewValue || Views.DAY
);

export const selectExcludedCalendars = createSelector(
  (state: DefaultRootState) => selectPlacePreferences(state, FILTER_PLACES.TEAM_VIEW_CALENDAR).excludedAgents?.list,
  (excludedAgentsList) => excludedAgentsList || []
);

export const selectCalendarExcludedStates = createSelector(
  (state: DefaultRootState) => selectPlacePreferences(state, FILTER_PLACES.TEAM_VIEW_CALENDAR).excludedStates?.value,
  (excludedStatesValue) => excludedStatesValue || []
);

export const selectShowInactiveCalendarEvents = createSelector(
  (state: DefaultRootState) => selectPlacePreferences(state, FILTER_PLACES.TEAM_VIEW_CALENDAR).showInactiveEvents?.value,
  (showInactiveEventsValue) => showInactiveEventsValue
);

export const selectCalendarSiderCollapsed = createSelector(
  (state: DefaultRootState) => selectPlacePreferences(state, FILTER_PLACES.TEAM_VIEW_CALENDAR).siderCollapsed?.value,
  (siderCollapsedValue) => siderCollapsedValue
);

export const selectShowBranchServiceGrouping = createSelector(
  (state: DefaultRootState) => selectPlacePreferences(state, FILTER_PLACES.TEAM_VIEW_CALENDAR).showBranchServiceGrouping?.value,
  (showBranchServiceGroupingValue) => showBranchServiceGroupingValue ?? true
);

export const selectShowAgentGrouping = createSelector(
  (state: DefaultRootState) => selectPlacePreferences(state, FILTER_PLACES.TEAM_VIEW_CALENDAR).showAgentGrouping?.value,
  (showAgentGroupingValue) => showAgentGroupingValue ?? true
);


const DAY_WINDOW_SIZE = 1
const THREE_DAY_WINDOW_SIZE = 2

export const selectCalendarDateWindow = createSelector(
  [
    (state: DefaultRootState, calendarDate?: string | number | Date) =>
      calendarDate || ServiceRequestSelectors.selectCalendarDate(state),
    selectCalendarView
  ],
  (calendarDate, calendarView) => {
    if (calendarView === Views.DAY) {
      // Add two days of buffer for day view to allow smoother navigation
      return [
        dayjs(calendarDate).subtract(DAY_WINDOW_SIZE, 'days').format('YYYY-MM-DD'),
        dayjs(calendarDate).add(DAY_WINDOW_SIZE, 'days').format('YYYY-MM-DD'),
      ];
    }

    if ([Views.WEEK, WEEK_AGENDA_VIEW].includes(calendarView)){
      return [
        dayjs(calendarDate).startOf('week').subtract(2, 'days').format('YYYY-MM-DD'),
        dayjs(calendarDate).endOf('week').add(2, 'days').format('YYYY-MM-DD'),
      ]
    }
    // Three day
    return [
      dayjs(calendarDate).subtract(THREE_DAY_WINDOW_SIZE, 'days').format('YYYY-MM-DD'),
      dayjs(calendarDate).add(THREE_DAY_WINDOW_SIZE, 'days').format('YYYY-MM-DD'),
    ];
  }
);

export const selectQueriedCalendarDateWindow = createSelector(
  [
    (state: DefaultRootState) => state,
    ServiceRequestSelectors.selectQueriedCalendarDate
  ],
  (state, calendarDate) => selectCalendarDateWindow(state, calendarDate)
);

export const selectCalendarFilters = (state: DefaultRootState) => selectFilters(state, FILTER_PLACES.TEAM_VIEW_CALENDAR)

export const selectAppointmentListFilters = (state: DefaultRootState) => selectFilters(state, FILTER_PLACES.APPOINTMENTS_TABLE)
export const selectAppointmentListPreferences = (state: DefaultRootState) => selectPlacePreferences(state, FILTER_PLACES.APPOINTMENTS_TABLE)
// endregion


// region Saved Views
const createSelectPlaceSettings = (place: string, scope?: string) => createSelector(
  (state: DefaultRootState) => {
    if (scope === SAVED_VIEW_SCOPES.PARTNER)
      return get(state, `preferences.partnerConfig.${place}`, EMPTY_OBJECT);
    return get(state, `preferences.list.ui.${place}`, EMPTY_OBJECT);
  },
  (settings) => settings
);

const placeSettingsCache = new Map<string, ReturnType<typeof createSelectPlaceSettings>>();

export const selectPlaceSettings = (state: DefaultRootState, place: string, scope?: string) => {
  const cacheKey = `${place}-${scope || 'default'}`;
  if (!placeSettingsCache.has(cacheKey)) {
    placeSettingsCache.set(cacheKey, createSelectPlaceSettings(place, scope));
  }
  return placeSettingsCache.get(cacheKey)!(state);
};

const selectActiveViewIdCache = new Map<string, any>();
export const selectActiveViewId = (state: DefaultRootState, place:string) => {
  if (!selectActiveViewIdCache.has(place)) {
    selectActiveViewIdCache.set(place, createSelector(
      (state: DefaultRootState) => selectPlaceSettings(state, place),
      (settings) => settings?.activeViewId || null
    ));
  }
  return selectActiveViewIdCache.get(place)!(state);
};

const selectPinnedViewsForPlaceCache = new Map<string, any>();
export const selectPinnedViewsForPlace = (state: DefaultRootState, place: string, scope?: string) => {
  const cacheKey = `${place}-${scope || 'default'}`;
  if (!selectPinnedViewsForPlaceCache.has(cacheKey)) {
    selectPinnedViewsForPlaceCache.set(cacheKey, createSelector(
      (state: DefaultRootState) => selectPlaceSettings(state, place, scope),
      (settings) => settings.pinnedViews || EMPTY_ARRAY
    ));
  }
  return selectPinnedViewsForPlaceCache.get(cacheKey)!(state);
};

const createSelectSavedViewsForPlace = (place: string, scope?: string) => createSelector(
  [
    (state: DefaultRootState) => get(state, `preferences.list.ui.${place}.savedViews`, EMPTY_ARRAY),
    (state: DefaultRootState) => get(state, `preferences.partnerConfig.${place}.savedViews`, EMPTY_ARRAY)
  ],
  (privateViews, partnerViewsRaw) => {
    const partnerViews = partnerViewsRaw.map(view => ({
      ...view,
      scope: SAVED_VIEW_SCOPES.PARTNER,
    }));

    if (scope === SAVED_VIEW_SCOPES.PARTNER) return partnerViews;
    if (scope === SAVED_VIEW_SCOPES.PRIVATE) return privateViews;

    return [...partnerViews, ...privateViews];
  }
);

const savedViewsForPlaceCache = new Map<string, ReturnType<typeof createSelectSavedViewsForPlace>>();

export const selectSavedViewsForPlace = (state: DefaultRootState, place: string, scope?: string) => {
  const cacheKey = `${place}-${scope || 'default'}`;
  if (!savedViewsForPlaceCache.has(cacheKey)) {
    savedViewsForPlaceCache.set(cacheKey, createSelectSavedViewsForPlace(place, scope));
  }
  return savedViewsForPlaceCache.get(cacheKey)!(state);
};

const selectActiveSavedViewIdCache = new Map<string, any>();
export const selectActiveSavedViewId = (state: DefaultRootState, place: string) => {
  if (!selectActiveSavedViewIdCache.has(place)) {
    selectActiveSavedViewIdCache.set(place, createSelector(
      (state: DefaultRootState) => get(state, `preferences.list.ui.${place}.activeView`),
      (activeView) => activeView
    ));
  }
  return selectActiveSavedViewIdCache.get(place)!(state);
};

const selectActiveSavedViewChangedCache = new Map<string, any>();
export const selectActiveSavedViewChanged = (state: DefaultRootState, place: string) => {
  if (!selectActiveSavedViewChangedCache.has(place)) {
    selectActiveSavedViewChangedCache.set(place, createSelector(
      (state: DefaultRootState) => get(state, `preferences.list.ui.${place}.activeViewChanged`),
      (activeViewChanged) => activeViewChanged
    ));
  }
  return selectActiveSavedViewChangedCache.get(place)!(state);
};
// endregion

// region General UI Preferences
export const selectKanbanBoardCollapsedStateSettings = createSelector(
  (state: DefaultRootState) => state?.preferences?.list?.ui?.kanbanBoardCollapsedStateSettings,
  (settings) => settings || EMPTY_OBJECT
);

export const isPreferencesListInitialized = createSelector(
  (state: DefaultRootState) => [state?.preferences?.list, state?.preferences?.initialized],
  ([preferencesList, initialized]) => !isEmpty(preferencesList) && initialized
);

export const selectReleaseNotesSeenVersion = createSelector(
  (state: DefaultRootState) => state?.preferences?.list?.ui?.releaseNotes?.seenVersion,
  (seenVersion) => seenVersion || null
);

export const selectSelectedSolution = createSelector(
  (state: DefaultRootState) => state?.preferences?.list?.ui?.selectedSolution?.value,
  (value) => value
);

export const selectIsHeatMap = createSelector(
  (state: DefaultRootState) => state?.preferences?.list?.ui?.isHeatmapList?.isHeatmapList,
  (isHeatmapList) => isHeatmapList
);

export const selectCalendarUISettings = createSelector(
  (state: DefaultRootState) => selectPlacePreferences(state, FILTER_PLACES.TEAM_VIEW_CALENDAR).calendarUISettings,
  (settings: CalendarUISettings | undefined): CalendarUISettings =>
    sanitizeCalendarUISettings(settings)
);

export const selectPinnedCalendarAgents = createSelector(
  (state: DefaultRootState) =>
    state?.preferences?.list?.ui?.pinnedCalendarAgents?.list || EMPTY_ARRAY,
  (pinnedCalendarAgents: string[]): string[] => pinnedCalendarAgents || EMPTY_ARRAY
);
export const selectSelectedTimezone = createSelector(
  (state: DefaultRootState) => state?.preferences?.list?.ui?.timezone?.value || Intl.DateTimeFormat().resolvedOptions().timeZone,
  (value) => value
);
// endregion

// region Partner Config & Integrations
export const selectPartnerConfig = createSelector(
  (state: DefaultRootState) => state?.preferences?.partnerConfig,
  (partnerConfig) => partnerConfig || EMPTY_OBJECT
);

export const selectCustomerRequiredFields = createSelector(
  (state: DefaultRootState) => state?.preferences?.partnerConfig?.customer_required_fields,
  (customerRequiredFields) => customerRequiredFields || EMPTY_ARRAY
);

export const selectShowPartnerLogoInHeader = createSelector(
  // The /partners/config GET nests keys under `data.config`, so fetchPartnerConfig lands
  // them at `partnerConfig.config.*`. Fall back to the flat shape for safety.
  (state: DefaultRootState) =>
    state?.preferences?.partnerConfig?.config?.[PARTNER_CONFIG_KEYS.SHOW_PARTNER_LOGO_IN_HEADER] ??
    state?.preferences?.partnerConfig?.[PARTNER_CONFIG_KEYS.SHOW_PARTNER_LOGO_IN_HEADER],
  (showPartnerLogoInHeader) => Boolean(showPartnerLogoInHeader)
);

const NO_CONTACT_ROLES: string[] = [];

// The partner's own list of contact roles; stored like the logo flag (see above).
export const selectContactRoles = createSelector(
  (state: DefaultRootState) => {
    const partnerConfig = state?.preferences?.partnerConfig as Record<string, any> | undefined;
    return partnerConfig?.config?.[PARTNER_CONFIG_KEYS.CONTACT_ROLES] ?? partnerConfig?.[PARTNER_CONFIG_KEYS.CONTACT_ROLES];
  },
  (value): string[] =>
    Array.isArray(value?.roles)
      ? value.roles.filter((role: unknown): role is string => typeof role === 'string' && role.trim() !== '')
      : NO_CONTACT_ROLES,
);

export const selectTaskAutomationList = createSelector(
  (state: DefaultRootState) => state?.preferences?.partnerConfig?.taskAutomationList,
  (taskAutomationList) => taskAutomationList || EMPTY_ARRAY
);

export const selectWidgetApiAppointmentBookingIntegration = createSelector(
  (state: DefaultRootState) => state?.preferences?.partnerConfig?.widgetApiAppointmentBookingIntegration,
  (integration) => integration || EMPTY_OBJECT
);

export const selectPartnerUserIntegrations = createSelector(
  (state: DefaultRootState) => state?.preferences?.userIntegrations,
  (userIntegrations) => userIntegrations
);

export const selectIntegrationTypes = createSelector(
  (state: DefaultRootState) => state?.preferences?.integrationTypes,
  (integrationTypes) => integrationTypes || EMPTY_ARRAY
);
// endregion

// region Lead Settings Lists
export const selectPriorityList = createSelector(
  (state: DefaultRootState) => get(state, 'preferences.list.lead.priorities', EMPTY_ARRAY),
  (priorities) => priorities
);

export const selectEmailAutomationTypesList = createSelector(
  (state: DefaultRootState) => get(state, 'preferences.list.lead.email_automation_types', EMPTY_ARRAY),
  (emailAutomationTypes) => emailAutomationTypes
);

export const selectStateTypesList = createSelector(
  (state: DefaultRootState) => get(state, 'preferences.list.lead.states_types', EMPTY_OBJECT),
  (stateTypes: Record<string, any>) => {
    return Object.keys(stateTypes).map(stateType => ({
      id: stateTypes[stateType].value,
      title: stateTypes[stateType].label,
    }));
  }
);
// endregion


